import React from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowRight } from "lucide-react";
import { CONTACT_PHONE, CONTACT_WHATSAPP } from "../../constants/data";

const GOLD = "#50C878";
const NAVY = "#013220";

// WhatsApp glyph reused across CTAs.
function WhatsAppIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

// Primary gold button — used as a link, route link, or onClick action.
export function CtaButton({ label, to, href, onClick, variant = "primary", icon, style = {} }) {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "13px 30px",
    borderRadius: "6px",
    fontFamily: "'Inter', sans-serif",
    fontWeight: 700,
    fontSize: "0.92rem",
    letterSpacing: "0.04em",
    textDecoration: "none",
    cursor: "pointer",
    transition: "transform 0.2s, box-shadow 0.2s, background 0.2s",
    border: variant === "outline" ? `1px solid ${variant === "outline" ? "rgba(1,50,32,0.35)" : GOLD}` : "none",
    background: variant === "primary" ? "linear-gradient(135deg, #50C878, #7FD9A0)" : "transparent",
    color: variant === "primary" ? "#FFFFFF" : NAVY,
    ...style,
  };

  const hoverIn = (e) => {
    if (variant === "primary") {
      e.currentTarget.style.transform = "translateY(-2px)";
      e.currentTarget.style.boxShadow = "0 8px 24px rgba(80,200,120,0.45)";
    } else {
      e.currentTarget.style.background = "rgba(80,200,120,0.10)";
    }
  };
  const hoverOut = (e) => {
    if (variant === "primary") {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "none";
    } else {
      e.currentTarget.style.background = "transparent";
    }
  };

  const content = (
    <>
      {icon}
      {label}
      {!icon && variant === "primary" && <ArrowRight size={16} />}
    </>
  );

  if (to) return <Link to={to} style={base} onMouseOver={hoverIn} onMouseOut={hoverOut}>{content}</Link>;
  if (href) return <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" style={base} onMouseOver={hoverIn} onMouseOut={hoverOut}>{content}</a>;
  return <button onClick={onClick} style={base} onMouseOver={hoverIn} onMouseOut={hoverOut}>{content}</button>;
}

// Full-width "always be able to contact us" band. Drop at the bottom
// of every page so the site stays CTA-centric.
export default function CtaSection({
  eyebrow = "Ready to get started?",
  title = "Let's find your home — together.",
  subtitle = "Register your interest and our team will reach out with group-pricing options tailored to you.",
  primaryLabel = "Contact Us",
  primaryTo = "/contact",
  onPrimaryClick,
}) {
  return (
    <section style={{ background: "linear-gradient(135deg, #013220 0%, #024A30 100%)", padding: "64px 24px" }}>
      <div style={{ maxWidth: "860px", margin: "0 auto", textAlign: "center" }}>
        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.72rem", color: GOLD, letterSpacing: "0.18em", textTransform: "uppercase" }}>
          {eyebrow}
        </span>
        <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.8rem, 4vw, 2.8rem)", fontWeight: 600, color: "#FFFFFF", margin: "12px 0 14px" }}>
          {title}
        </h2>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "1rem", color: "rgba(255,255,255,0.78)", lineHeight: 1.7, maxWidth: "560px", margin: "0 auto 30px" }}>
          {subtitle}
        </p>
        <div style={{ display: "flex", gap: "14px", justifyContent: "center", flexWrap: "wrap" }}>
          {onPrimaryClick
            ? <CtaButton label={primaryLabel} onClick={onPrimaryClick} />
            : <CtaButton label={primaryLabel} to={primaryTo} />}
          <CtaButton
            label="WhatsApp Us"
            href={CONTACT_WHATSAPP}
            variant="outline"
            icon={<WhatsAppIcon />}
            style={{ color: "#FFFFFF", borderColor: "rgba(255,255,255,0.45)" }}
          />
          <CtaButton
            label={`Call +91 ${CONTACT_PHONE}`}
            href={`tel:+91${CONTACT_PHONE}`}
            variant="outline"
            icon={<Phone size={16} />}
            style={{ color: "#FFFFFF", borderColor: "rgba(255,255,255,0.45)" }}
          />
        </div>
      </div>
    </section>
  );
}
